import classNames from 'classnames'
import { t } from 'i18next'
import { Dropdown } from 'primereact/dropdown'
import { Tooltip } from 'primereact/tooltip'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Apprentice } from '../../../../../model/person.model'
import { useAppSelector } from '../../../../../redux/hooks'
import { findApprenticeById } from '../../../../../util/arraySearch'
import { sortByFirstName } from '../../../../../util/sortByFirstName'
import { AvatarWithName } from '../../../AvatarWithName'
import { getFormErrorMessage, getRequiredFormMessage } from '../../../ErrorText'

export const ApprenticeDropdown = (props: {
  isDisabled?: boolean
  fieldName?: string
}) => {
  const apprentices: Apprentice[] = useAppSelector((state) => {
    const apps = state.apprentices
    const filteredApprentices = apps.filter((a) => a.is_active === 1)
    return filteredApprentices.sort(sortByFirstName)
  })

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext()

  const selectedApprenticeTemplate = (apprenticeId: number | undefined) => {
    if (apprenticeId && apprenticeId > 0) {
      const apprentice = findApprenticeById(apprenticeId, apprentices)
      if (apprentice) {
        return <AvatarWithName person={apprentice} />
      }
    }
    return undefined
  }

  /**
   * Return the apprentice to select as option with avatar and full name
   * If the apprentice is not yet configured with a profession, do not show it as option,
   * since it has no apprentice_id and can not be mapped with documents
   * @param {Apprentice} person
   * @returns {JSX.Element | undefined}
   */
  const apprenticeOptionTemplate = (person: Apprentice): JSX.Element => {
    if (!person?.apprentice) {
      // apprentice is basically a person with missing apprentice data
      return (
        <>
          <div className={`no-selector tooltip_pers_${person.person_id}`}>
            <span className={'gray-scale'}>
              <AvatarWithName person={person} />
            </span>
            <i
              className={
                'fas fa-exclamation-triangle icon-red ml-2 warning-icon'
              }
            />
          </div>
          <Tooltip
            target={`.tooltip_pers_${person.person_id}`}
            content={t<string>('apprentice.set_data')}
            position={'top'}
          />
        </>
      )
    }
    if (person.is_active === 0) {
      return (
        <>
          <div className={'inactive-row'}>
            <span className={'gray-scale'}>
              <AvatarWithName person={person} />
            </span>
            <i
              className={'fa-solid fa-person-circle-question icon-grey ml-2'}
            />
          </div>
          <Tooltip
            target={`.tooltip_pers_${person.person_id}`}
            content={t<string>('core.person.inactive')}
            position={'top'}
          />
        </>
      )
    }
    return <AvatarWithName person={person} />
  }

  return (
    <>
      <label
        htmlFor={'apprentice-dropdown'}
        className={classNames({ 'p-error': errors.id_apprentice })}
      >
        {t<string>('core.apprentice.neutral')}
      </label>
      <Controller
        name={props.fieldName ? props.fieldName : 'id_apprentice'}
        control={control}
        rules={{ required: getRequiredFormMessage('core.apprentice.neutral') }}
        render={({ field, fieldState }) => (
          <Dropdown
            id={field.name}
            appendTo={document.body}
            options={apprentices}
            defaultValue={field.value}
            onChange={(e) => {
              // we have the apprentice object and want to set the id
              const id = e.target.value?.apprentice?.apprentice_id
              field.onChange(id)
              setValue(field.name, id, {
                shouldDirty: true,
              })
            }}
            optionLabel="full_name"
            placeholder={t<string>('core.select')}
            filter
            showFilterClear
            showClear
            filterBy="full_name"
            valueTemplate={selectedApprenticeTemplate(field.value)}
            itemTemplate={apprenticeOptionTemplate}
            className={classNames(
              { 'p-invalid': fieldState.error },
              ' people-dropdown w-full'
            )}
            emptyMessage={t<string>('core.empty_message')}
            emptyFilterMessage={t<string>('core.empty_filter_message')}
            disabled={props?.isDisabled}
          />
        )}
      />
      {getFormErrorMessage(errors, 'id_apprentice')}
    </>
  )
}